import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { shared } from "../styles";
import { appColorScheme } from "../contexts/theme-context";
import { colorSchemes, type ColorScheme, colorSchemeLabels } from "@pentacode/core";
import { SignalWatcher } from "@lit-labs/signals";

export type ColorSchemeChangeEvent = CustomEvent<ColorScheme>;

@customElement("ptc-color-scheme-select")
export class ColorSchemeSelect extends SignalWatcher(LitElement) {
    @property()
    value: ColorScheme = "system";

    private _onColorSchemeChange(e: Event) {
        const value = (e.target as HTMLSelectElement).value as ColorScheme;
        this.value = value;
        this.dispatchEvent(
            new CustomEvent<ColorScheme>("color-change", {
                detail: value,
            })
        );
    }

    static styles = [
        shared,
        css`
            :host {
                display: inline-block;
                font: inherit;
            }

            select {
                padding: 0.5em 0.75em;
                border: 1px solid var(--shade-2);
                border-radius: 0.25em;
                background-color: var(--background);
                color: var(--text);
                cursor: pointer;
                font-family: inherit;
                font-size: inherit;
                flex: 1;
            }

            select:focus {
                outline: none;
                border-color: var(--blue);
            }

            .select-wrapper {
                flex: 1;
                position: relative;
                display: flex;
                align-items: center;
                padding: 0;
                font: inherit;
            }

            .select-wrapper select {
                padding-right: 2rem;
            }

            option {
                padding: 0.5em;
            }
        `,
    ];

    render() {
        return html`
            <label for="color-scheme" class="select-wrapper" title="Farbschema">
                <slot></slot>
                <select aria-label=${colorSchemeLabels.aria} @change=${this._onColorSchemeChange} .value=${this.value}>
                    ${colorSchemes.map(
                        (scheme) =>
                            html` <option ?selected=${scheme === this.value} value=${scheme}>
                                ${colorSchemeLabels[scheme]}
                            </option>`
                    )}
                </select>
            </label>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-color-scheme-select": ColorSchemeSelect;
    }
}
